/**
 * Which of the user's lists were created recently, newest first.
 *
 * None of the three stores records when a list was made — portfolios, tag
 * watchlists and headline queries carry no timestamps — so the builder cannot
 * ask the server what is new. Without this, a list you had just saved was
 * filed alphabetically among twenty others, and "did that work?" meant
 * scanning for it.
 *
 * Noted by the three create calls themselves rather than by the builder:
 * lists are also made by the concierge, onboarding and the widget filter
 * panels, and "recently created" should mean all of them. Per browser, which
 * is the honest limit of a client-side record — a list made on another
 * machine simply files alphabetically, as everything did before.
 *
 * Same subscribe/snapshot shape as ./prebuiltWatchlists.
 */

export type CreatedListKind = "watchlist" | "tag" | "combined";

export type CreatedList = {
  /** See `createdListKey`. */
  key: string;
  /** Epoch ms. */
  at: number;
};

const STORAGE_KEY = "nq.watchlists.recentlyCreated";
/** More than the builder shows, so deleting a few doesn't empty the section. */
const MAX_ENTRIES = 20;

const PREFIX: Record<CreatedListKind, string> = {
  watchlist: "p",
  tag: "t",
  combined: "q",
};

/**
 * One key per list across all three stores. Their ids are independent
 * sequences, so the store has to be part of it — portfolio 12 and combined
 * watchlist 12 are different lists.
 */
export const createdListKey = (kind: CreatedListKind, id: number): string =>
  `${PREFIX[kind]}-${id}`;

const read = (): CreatedList[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (e): e is CreatedList =>
            typeof e?.key === "string" && typeof e?.at === "number"
        )
      : [];
  } catch {
    return [];
  }
};

let snapshot: CreatedList[] = read();
const listeners = new Set<() => void>();

const commit = (next: CreatedList[]): void => {
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / quota — the section just won't survive a reload.
  }
  for (const listener of listeners) listener();
};

export function noteListCreated(
  kind: CreatedListKind,
  id: number | undefined
): void {
  // A create whose response didn't carry an id can't be matched to a row
  // later, so there is nothing useful to record.
  if (typeof id !== "number" || !Number.isFinite(id)) return;
  const key = createdListKey(kind, id);
  commit(
    [{ key, at: Date.now() }, ...snapshot.filter((e) => e.key !== key)].slice(
      0,
      MAX_ENTRIES
    )
  );
}

export function forgetCreatedLists(keys: string[]): void {
  const drop = new Set(keys);
  if (!snapshot.some((e) => drop.has(e.key))) return;
  commit(snapshot.filter((e) => !drop.has(e.key)));
}

/**
 * A list made in a popout window's filter panel writes from that window, so
 * the main one picks the change up from the storage event.
 */
const onStorage = (event: StorageEvent) => {
  if (event.key !== STORAGE_KEY) return;
  snapshot = read();
  for (const listener of listeners) listener();
};

export function subscribeToRecentlyCreated(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

export function getRecentlyCreatedSnapshot(): CreatedList[] {
  return snapshot;
}
